// src/components/BoardPack/components/consolidate_boardpack_components/Budget_Actual_Chart_component_v2.jsx
//
// V2 — alag copy, taake original `Budget_Actual_Chart_component.jsx` ko
// kabhi bhi touch na karna pade.
//
// FINAL LAYOUT (reference screenshot ke mutabiq): Revenue aur Cost har ek
// apna POORA, alag, full-size panel hai — apna title, apna legend, normal
// size labels, aur neeche ek "REVENUE"/"COSTS" caption. Do rows: Dec row
// (Revenue panel + Cost panel) aur YTD row (Revenue panel + Cost panel).
//
// UPDATE: 
//  1) Value label ab sign se position hota hai: positive => bar ke UPAR,
//     negative => bar ke NEECHE.
//  2) Bars ke darmiyan connector lines (patli border-lines) add ki gayi hain.
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);
Chart.defaults.color = THEME.colors.black; // global fallback so axis text is readable on white bg

// Har number ko "153,618" jaisa format karta hai; negative ko (497) style
const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "-";
  const abs = Math.abs(Math.round(n));
  const str = abs.toLocaleString("en-US");
  return n < 0 ? `(${str})` : str;
};

// Waterfall math: pehla/aakhri item "total" (full bar, base 0), darmiyan
// wale items floating bars — running total se offset. type na diya ho to
// khud decide: pehla/aakhri = total, baaki: value>=0 => increase, else decrease
const buildWaterfall = (series) => {
  if (!series?.length) return [];

  let running = 0;
  return series.map((item, idx) => {
    const isEdge = idx === 0 || idx === series.length - 1;
    const isTotal = item.type
      ? String(item.type).trim().toLowerCase() === "total"
      : isEdge;

    if (isTotal) {
      running = item.value;
      return { label: item.label, from: 0, to: item.value, display: item.value, type: "total" };
    }

    // Sign hi direction aur colour decide karta hai, is liye dono kabhi alag nahi hongay.
    const type = item.value >= 0 ? "increase" : "decrease";
    const start = running;
    const end = running + item.value;
    running = end;

    return {
      label: item.label,
      from: Math.min(start, end), // neeche wala edge
      to: Math.max(start, end),   // upar wala edge
      display: item.value,
      type,
    };
  });
};

// Lambe labels ko truncate karta hai taake tight bar-spacing mein bhi
// overlap na ho.
const truncateLabel = (label, maxChars = 18) => {
  if (!label) return "";
  if (label.length <= maxChars) return label;
  return label.slice(0, maxChars - 1).trimEnd() + "…";
};

const barColor = (type) => {
  const t = String(type || "").trim().toLowerCase();
  if (t === "increase") return THEME.colors.increase;
  if (t === "decrease") return THEME.colors.decrease;
  return THEME.colors.total;
};

// ✅ Value label plugin — SIGN ke hisaab se position:
//    positive value => bar ke UPAR
//    negative value => bar ke NEECHE
const valueLabelPlugin = {
  id: "valueLabelPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const yScale = scales.y;

    ctx.save();
    ctx.font = "600 9px sans-serif";
    ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
    ctx.textAlign = "center";

    meta.data.forEach((bar, i) => {
      const raw = chart.data.datasets[0].rawData[i];
      const pixelFrom = yScale.getPixelForValue(raw.from);
      const pixelTo = yScale.getPixelForValue(raw.to);
      const visualTop = Math.min(pixelFrom, pixelTo);
      const visualBottom = Math.max(pixelFrom, pixelTo);

      if (raw.display >= 0) {
        // positive → bar ke UPAR
        ctx.textBaseline = "bottom";
        ctx.fillText(fmt(raw.display), bar.x, visualTop - 4);
      } else {
        // negative → bar ke NEECHE
        ctx.textBaseline = "top";
        ctx.fillText(fmt(raw.display), bar.x, visualBottom + 4);
      }
    });
    ctx.restore();
  },
};

// ✅ Connector lines — har bar ke end level se agli bar ke start level tak
// patli line (waterfall ki border/connector lines)
const connectorPlugin = {
  id: "connectorPlugin",
  beforeDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const raws = chart.data.datasets[0].rawData;
    const yScale = scales.y;

    ctx.save();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
    ctx.lineWidth = 1;

    for (let i = 0; i < meta.data.length - 1; i++) {
      const raw = raws[i];
      // is bar ke khatam hone ka level (running total)
      const endValue =
        raw.type === "total"
          ? raw.display
          : raw.type === "increase"
          ? raw.to
          : raw.from;

      const y = Math.round(yScale.getPixelForValue(endValue)) + 0.5;
      const cur = meta.data[i];
      const next = meta.data[i + 1];

      ctx.beginPath();
      ctx.moveTo(cur.x + cur.width / 2, y);
      ctx.lineTo(next.x - next.width / 2, y);
      ctx.stroke();
    }
    ctx.restore();
  },
};

// Custom legend renderer — draws Increase/Decrease/Total swatches + white
// text directly on each panel's own canvas, at the top of that panel.
const customLegendPlugin = {
  id: "customLegendPlugin",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const items = [
      { text: "Increase", color: THEME.colors.increase },
      { text: "Decrease", color: THEME.colors.decrease },
      { text: "Total", color: THEME.colors.total },
    ];

    ctx.save();
    ctx.font = "600 10px sans-serif";
    ctx.textBaseline = "middle";

    const boxSize = 8;
    const gapAfterBox = 5;
    const gapBetweenItems = 14;
    const widths = items.map((it) => boxSize + gapAfterBox + ctx.measureText(it.text).width);
    const totalWidth = widths.reduce((a, b) => a + b, 0) + gapBetweenItems * (items.length - 1);

    let x = chartArea.left + (chartArea.right - chartArea.left - totalWidth) / 2;
    const y = 14;

    items.forEach((item, i) => {
      ctx.fillStyle = item.color;
      ctx.fillRect(x, y - boxSize / 2, boxSize, boxSize);

      ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
      ctx.fillText(item.text, x + boxSize + gapAfterBox, y);

      x += widths[i] + gapBetweenItems;
    });

    ctx.restore();
  },
};

// X-axis labels ko stagger karke draw karta hai — chartArea ke bottom edge
// pe (zero-line ke paas nahi), taake negative bars ke value-labels se
// kabhi na takraye. Even index upar row, odd index neeche row.
const staggeredLabelPlugin = {
  id: "staggeredLabelPlugin",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);

    ctx.save();
    ctx.font = "600 8px sans-serif";
    ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const baseY = chartArea.bottom + 4;
    const rowGap = 13;

    meta.data.forEach((bar, i) => {
      const label = truncateLabel(chart.data.labels[i]);
      if (!label) return;
      const y = baseY + (i % 2 === 0 ? 0 : rowGap);
      ctx.fillText(label, bar.x, y);
    });

    ctx.restore();
  },
};

// ── Ek POORA, full-size waterfall panel (Revenue YA Cost, apna khud ka) ──
function FullWaterfallPanel({ panelTitle, caption, data }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const hasData = Array.isArray(data) && data.length > 0;

  useEffect(() => {
    if (!canvasRef.current) return;

    if (!hasData) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const waterfall = buildWaterfall(data);

    const maxVal = Math.max(...waterfall.map((d) => d.to), 0);
    const yMax = maxVal > 0 ? maxVal * 1.18 : undefined;

    // ✅ 1.15 → 1.25: negative labels ab bar ke neeche aate hain, unke liye
    // extra jagah chahiye taake x-axis labels se na takrayein.
    const minVal = Math.min(...waterfall.map((d) => d.from), 0);
    const yMin = minVal < 0 ? minVal * 1.25 : undefined;

    // Chhoti bars (jo scale ke saamne invisible ho jati hain) ko minimum
    // visible height dete hain — sirf visual ke liye, label/tooltip mein
    // asal (real) value hi dikhegi.
    const range = (maxVal > 0 ? maxVal : 0) - Math.min(minVal, 0);
    const MIN_VISIBLE = range * 0.015;

    const waterfallVisible = waterfall.map((d) => {
      if (d.type === "total") return d;
      const height = d.to - d.from;
      if (height > 0 && height < MIN_VISIBLE) {
        return d.display >= 0
          ? { ...d, to: d.from + MIN_VISIBLE }  // increase: start se upar ki taraf
          : { ...d, from: d.to - MIN_VISIBLE }; // decrease: start se neeche ki taraf
      }
      return d;
    });

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: waterfall.map((d) => d.label),
        datasets: [
          {
            data: waterfallVisible.map((d) => [d.from, d.to]),
            rawData: waterfall,
            backgroundColor: waterfall.map((d) => barColor(d.type)),
            borderWidth: 0,
            barPercentage: 0.55,
          },
        ],
      },
      options: {
        responsive: false, // ChartTableComponent jaisa manual control — auto-resize race hi blur ki asal wajah thi
        maintainAspectRatio: false,
        clip: false,
        layout: { padding: { top: 30, bottom: 32 } },
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),

        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => fmt(ctx.dataset.rawData[ctx.dataIndex].display),
            },
          },
        },
        scales: {
          x: {
            ticks: { display: false },
            grid: { display: false },
          },
          y: {
            max: yMax,
            min: yMin,
            ticks: {
              color: THEME.colors.black,
              font: { size: 10 },
              callback: (v) => fmt(v),
            },
            grid: { display: false },
          },
        },
      },
      // ✅ connectorPlugin add kiya (sabse pehle, taake bars ke peeche draw ho)
      plugins: [connectorPlugin, valueLabelPlugin, customLegendPlugin, staggeredLabelPlugin],
    });

    // ✅ ChartTableComponent jaisa manual resize — responsive:true ka apna
    // internal resize-timing container ke final layout size se race kar
    // jaata tha (khaas kar PDF/screenshot capture ke waqt), jisse canvas
    // galat size pe ban ke stretch-blur ho jata tha. Yahan khud container
    // ka exact offsetWidth/offsetHeight measure kar ke chart.resize(w, h)
    // explicitly call karte hain — pehle mount ke turant baad, phir jab
    // bhi container ka size badle (ResizeObserver).
    requestAnimationFrame(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.resize(
          containerRef.current.offsetWidth,
          containerRef.current.offsetHeight
        );
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w = containerRef.current.offsetWidth;
      const h = containerRef.current.offsetHeight;
      chartRef.current.resize(w, h);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, hasData]);

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: THEME.colors.white, // was chartDarkBg
        borderRadius: "4px",
        border: `1px solid ${THEME.colors.black}`,
        p: 1.5,
        minWidth: 0,
      }}
      className="!border"
    >
      <Typography
        align="center"
        sx={{
          color: THEME.colors.black,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          mb: 0.5,
          textTransform: "uppercase",
        }}
      >
        {panelTitle}
      </Typography>

      <Box ref={containerRef} sx={{ height: 300, position: "relative" }}>
        {hasData ? (
          <canvas ref={canvasRef} />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: THEME.colors.black, opacity: 0.6, fontSize: 12 }}>
              No data available for this period
            </Typography>
          </Box>
        )}
      </Box>

      {caption && (
        <Typography
          align="center"
          sx={{
            color: THEME.colors.black, // was white
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 1,
            mt: 0.5,
            textTransform: "uppercase",
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}

// ── Main presentational component (NO API calls here — page does that) ──
// decSeries / ytdSeries { revenue: [...], cost: [...] } shape mein aate hain.
// Titles bhi ab per-segment aate hain (page banata hai): decRevenueTitle,
// decCostTitle, ytdRevenueTitle, ytdCostTitle.
const Budget_Actual_Chart_component_v2 = ({
  title,
  subtitle,
  showComments = true,
  decSeries = { revenue: [], cost: [] },
  ytdSeries = { revenue: [], cost: [] },
  decRevenueTitle = "",
  decCostTitle = "",
  ytdRevenueTitle = "",
  ytdCostTitle = "",
  logoSrc = null,
}) => {
  const [comments, setComments] = useState("");

  const hasDecData =
    (decSeries?.revenue?.length ?? 0) > 0 || (decSeries?.cost?.length ?? 0) > 0;
  const hasYtdData =
    (ytdSeries?.revenue?.length ?? 0) > 0 || (ytdSeries?.cost?.length ?? 0) > 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: THEME.colors.white,
        paddingX: 2,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* ── Title ── */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {title && (
          <HeadingComponent variant="main">{title}</HeadingComponent>
        )}
        {subtitle && (
          <HeadingComponent variant="main">{subtitle}</HeadingComponent>
        )}
      </Box>

      {/* ── Dec row: Revenue panel + Cost panel — sirf tab dikhta hai jab
          data.month API se aaya ho. Agar dono series khaali hain to poori
          row hi skip ho jati hai (khaali "No data" placeholder boxes ki
          zaroorat nahi). ── */}
      {hasDecData && (
        <Box sx={{ display: "flex", gap: 2, mb: 2 }} className="!border p-1">
          <FullWaterfallPanel panelTitle={decRevenueTitle} caption="Revenue" data={decSeries?.revenue} />
          <FullWaterfallPanel panelTitle={decCostTitle} caption="Costs" data={decSeries?.cost} />
        </Box>
      )}

      {/* ── YTD row: Revenue panel + Cost panel ── */}
      {hasYtdData && (
        <Box sx={{ display: "flex", gap: 2 }} className="!border p-1">
          <FullWaterfallPanel panelTitle={ytdRevenueTitle} caption="Revenue" data={ytdSeries?.revenue} />
          <FullWaterfallPanel panelTitle={ytdCostTitle} caption="Costs" data={ytdSeries?.cost} />
        </Box>
      )}

      {/* ── Comments ── */}
      {showComments && (
        <div className="!border-2 mt-3">
          <div className="border border-black p-2 bg-white">
            <div className="text-center font-semibold text-sm mb-1 border-b border-transparent">
              Comments
            </div>
            <textarea
              className="w-full h-18 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>
      )}
    </Box>
  );
};

export default Budget_Actual_Chart_component_v2;